import React from 'react';

/**
 * One labelled control on the contact page — the only place the module takes
 * input, so the design's field is described here once and reused eight times.
 *
 * Measured off Figma nodes 16383:29851 (input) and 16383:29870 (text area):
 *   label    13px regular #162447, 8px above the control
 *   control  44px tall, 12px radius, ground #f5f7fa, 16px of side padding,
 *            14px text, placeholder #6a7282 — no border at rest
 *   area     108px tall, 12px of top and bottom padding, 21px leading
 *
 * The design draws no focus or error state. The focus ring is the module's own
 * — the same brand ring every button already uses, because a field with no
 * visible focus is unusable by keyboard — and the error is a red hairline plus
 * a line of text underneath, which is the least the form can do and still say
 * what went wrong.
 *
 * `required` renders the design's asterisk, and marks the control required for
 * assistive tech, but validation itself belongs to the hook: the browser's own
 * bubbles are unstyled, English, and would contradict the messages the hook
 * already writes in the reader's language.
 *
 * WHY THE `!` UTILITIES
 *
 * public/css/rtl.css and its LTR twin are loaded globally from index.html, and
 * both carry an unscoped rule on the bare element selectors:
 *
 *     input, textarea { background-color: #f9faff !important;
 *                       border-radius: 6px !important;
 *                       border: none !important; padding: 1rem; }
 *
 * It reaches every field in the module and cannot be outranked by an ordinary
 * utility, so the ground came out pale blue at a 6px radius — and `border: none`
 * meant the invalid state drew nothing at all, which is the part that mattered.
 * The three declarations it forces are forced back here; among `!important`
 * declarations specificity decides, and a class beats an element selector.
 *
 * Its `padding` is not `!important`, so `tw-px-4` already wins on the sides;
 * only the vertical padding needs saying, and only on the input, since the text
 * area sets its own.
 *
 * The fix lives here rather than in base.css because the error border has to
 * stay a per-field decision: a blanket `border-color` reset scoped to
 * `.v2-root input` would outrank this component's own choice and paint the
 * invalid state transparent again.
 */
const Field = ({
    id,
    label,
    required = false,
    error,
    as = 'input',
    className = '',
    ...rest
}) => {
    const Control = as === 'textarea' ? 'textarea' : 'input';
    const errorId = error ? `${id}-error` : undefined;

    return (
        <div className={`tw-flex tw-flex-col tw-gap-2 ${className}`}>
            <label htmlFor={id} className="tw-text-[13px] tw-font-normal tw-leading-[13px] tw-text-v2-text-deep">
                {label}
                {required && <span aria-hidden> *</span>}
            </label>

            <Control
                id={id}
                aria-required={required || undefined}
                aria-invalid={error ? true : undefined}
                aria-describedby={errorId}
                className={[
                    'tw-w-full !tw-rounded-v2-btn !tw-bg-v2-ground-200 tw-px-4',
                    'tw-text-[14px] tw-text-v2-text-deep tw-text-start',
                    'placeholder:tw-text-v2-text-faint',
                    // `border-solid` as well as the width: the global rule's
                    // `border: none` sets the style, not just the width, and a
                    // 1px border of style `none` still draws nothing.
                    '!tw-border !tw-border-solid tw-transition tw-duration-200',
                    'focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-v2-brand/40',
                    error ? '!tw-border-v2-error' : '!tw-border-transparent',
                    // The area is the same field grown taller, so it keeps the
                    // ground, radius and side padding and changes only what the
                    // design changes: height, vertical padding and leading.
                    as === 'textarea'
                        ? 'tw-h-[108px] tw-resize-y tw-py-3 tw-leading-[21px]'
                        : 'tw-h-11 tw-py-0',
                ].join(' ')}
                {...rest}
            />

            {error && (
                <p id={errorId} className="tw-text-[12px] tw-leading-[18px] tw-text-v2-error">
                    {error}
                </p>
            )}
        </div>
    );
};

export default Field;
